export async function fetchSurveyPreview(apiUrl, surveyId) {
    const response = await fetch(`${apiUrl}api/survey/${surveyId}?show_preview=true`, {
        method: 'GET',
        headers: { accept: 'application/json' },
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch survey preview: ${response.status}`);
    }

    return response.json();
}

// keepalive: a request started right before a navigation (Thank You redirect,
// close on unload) is still delivered by the browser
function postJson(url, payload, options = {}) {
    return fetch(url, {
        method: 'POST',
        headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        keepalive: true,
        ...options,
    });
}

// `options` is forwarded to fetch (e.g. an AbortSignal for the view gate)
export function viewSurvey(apiUrl, surveyUuid, payload, options = {}) {
    return postJson(`${apiUrl}api/surveys/${surveyUuid}/view`, payload, options);
}

export function viewQuestion(apiUrl, questionUuid, payload) {
    return postJson(`${apiUrl}api/questions/${questionUuid}/view`, payload);
}

export function trackBehavior(apiUrl, questionUuid, payload) {
    return postJson(`${apiUrl}api/questions/${questionUuid}/behavior`, payload);
}

export function respondQuestion(apiUrl, questionUuid, payload) {
    return postJson(`${apiUrl}api/questions/${questionUuid}/respond`, payload);
}

export function completeSurvey(apiUrl, surveyUuid, payload) {
    return postJson(`${apiUrl}api/surveys/${surveyUuid}/complete`, payload);
}

export function identifyUser(apiUrl, payload) {
    return fetch(`${apiUrl}api/identify`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
        },
        body: JSON.stringify(payload),
    });
}

export function trackPurchase(apiUrl, payload) {
    return fetch(`${apiUrl}api/purchase`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
        },
        body: JSON.stringify(payload),
    });
}

const ATTRIBUTES_CACHE_MAX_AGE = 30 * 60 * 1000; // 30 minutes

function attributesCacheKey(projectId, userId) {
    return `ph_attributes_${projectId}_${userId}`;
}

// Attributes cached for this tab, or null when there is none / it expired.
export function readUserAttributesCache(projectId, userId) {
    if (userId === null || userId === undefined || userId === '') return null;
    try {
        const cached = sessionStorage.getItem(attributesCacheKey(projectId, userId));
        if (!cached) return null;
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < ATTRIBUTES_CACHE_MAX_AGE) {
            return data;
        }
    } catch {
        // Invalid cache
    }
    return null;
}

// Also used to persist attributes updated locally after a purchase, so a
// page reload keeps them until the backend has aggregated the order.
export function storeUserAttributesCache(projectId, userId, data) {
    if (userId === null || userId === undefined || userId === '') return;
    try {
        sessionStorage.setItem(
            attributesCacheKey(projectId, userId),
            JSON.stringify({
                data,
                timestamp: Date.now(),
            }),
        );
    } catch {
        // Storage unavailable (private mode, quota)
    }
}

export async function getUserAttributes(apiUrl, projectId, userId) {
    // Never request `/api/attributes/{project_id}/null`
    if (userId === null || userId === undefined || userId === '') {
        return {};
    }

    const cached = readUserAttributesCache(projectId, userId);
    if (cached) {
        return cached;
    }

    try {
        const response = await fetch(`${apiUrl}api/attributes/${projectId}/${userId}`, {
            method: 'GET',
            headers: { accept: 'application/json' },
        });

        const data = response.ok ? await response.json() : {};
        storeUserAttributesCache(projectId, userId, data);
        return data;
    } catch {
        storeUserAttributesCache(projectId, userId, {});
        return {};
    }
}
